import React, { useState } from 'react';
import { ArrowRight, MapPin, Clock } from 'lucide-react';
import { estateTransformations } from '../mock';
import useReveal from '../hooks/useReveal';

/* Build a responsive <img> srcSet for a case-study image path.
   Variants live next to the source with `-400w.webp` / `-800w.webp` suffixes
   (generated at build-prep time via /app/frontend/public/*-400w.webp). */
const buildCaseSrcSet = (path) => {
  if (!path) return undefined;
  const base = path.replace(/\.(webp|png|jpg|jpeg)$/i, '');
  return `${base}-400w.webp 400w, ${base}-800w.webp 800w, ${path} 2000w`;
};
const CASE_SIZES = '(max-width: 640px) 400px, (max-width: 1024px) 800px, 1000px';

const BeforeAfterPair = ({ item, index }) => {
  const [showAfter, setShowAfter] = useState(true);

  return (
    <article
      className="reveal group relative rounded-[10px] overflow-hidden border border-white/10 bg-[color:var(--obsidian-2)] hover:border-[color:var(--green)]/40 transition-colors"
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 md:p-6 border-b border-white/10">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/45">{item.number}</div>
          <div className="min-w-0">
            <h3 className="font-display text-[20px] md:text-[24px] leading-tight text-[color:var(--parchment)]">
              {item.title}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/55">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {item.location}
              </span>
              <span className="text-[color:var(--green)]">{item.scope}</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> {item.duration}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile-only toggle to swap between before/after when stacked */}
        <div className="md:hidden inline-flex rounded-full border border-white/15 p-0.5 text-[12px] md:text-[10.5px] font-mono tracking-[0.22em] uppercase">
          <button
            onClick={() => setShowAfter(false)}
            className={`px-3 py-1 rounded-full transition-colors ${!showAfter ? 'bg-[color:var(--parchment)] text-[color:var(--obsidian)]' : 'text-[color:var(--parchment)]/60'}`}
          >
            Before
          </button>
          <button
            onClick={() => setShowAfter(true)}
            className={`px-3 py-1 rounded-full transition-colors ${showAfter ? 'bg-[color:var(--green)] text-[color:var(--obsidian)]' : 'text-[color:var(--parchment)]/60'}`}
          >
            After
          </button>
        </div>
      </div>

      {/* Image comparison — side by side on md+, toggled panel on mobile */}
      <div className="hidden md:grid grid-cols-2 divide-x divide-white/10">
        <div className="relative aspect-[4/3] overflow-hidden bg-[color:var(--obsidian)]">
          <img
            src={item.beforeImage}
            srcSet={buildCaseSrcSet(item.beforeImage)}
            sizes={CASE_SIZES}
            alt={`${item.title} \u2014 before`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[color:var(--obsidian)]/80 backdrop-blur border border-white/15 font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--parchment)]/60" /> Before
          </div>
        </div>
        <div className="relative aspect-[4/3] overflow-hidden bg-[color:var(--obsidian)]">
          <img
            src={item.afterImage}
            srcSet={buildCaseSrcSet(item.afterImage)}
            sizes={CASE_SIZES}
            alt={`${item.title} \u2014 after`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[color:var(--green)] font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--obsidian)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--obsidian)]" /> After
          </div>
        </div>
      </div>

      {/* Mobile: single image swaps */}
      <div className="md:hidden relative aspect-[4/3] overflow-hidden bg-[color:var(--obsidian)]">
        <img
          src={showAfter ? item.afterImage : item.beforeImage}
          srcSet={buildCaseSrcSet(showAfter ? item.afterImage : item.beforeImage)}
          sizes="(max-width: 640px) 400px, 800px"
          alt={`${item.title} \u2014 ${showAfter ? 'after' : 'before'}`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-opacity duration-500"
          key={showAfter ? 'a' : 'b'}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className={`absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase backdrop-blur border ${showAfter ? 'bg-[color:var(--green)] text-[color:var(--obsidian)] border-[color:var(--green)]' : 'bg-[color:var(--obsidian)]/80 border-white/15 text-[color:var(--parchment)]'}`}>
          {showAfter ? 'After' : 'Before'}
        </div>
      </div>

      {/* Summary strip */}
      <div className="p-5 md:p-6 border-t border-white/10 flex items-start gap-4">
        <ArrowRight className="shrink-0 w-4 h-4 mt-1 text-[color:var(--green)]" />
        <p className="text-[color:var(--parchment)]/75 text-[14.5px] leading-relaxed">{item.summary}</p>
      </div>
    </article>
  );
};

const EstatesTransformed = ({ onOpenVault }) => {
  const ref = useReveal({ threshold: 0.08 });

  return (
    <section id="vault" ref={ref} className="relative py-16 md:py-20 px-5 sm:px-6 md:px-10 border-t border-white/5 overflow-hidden">
      <div className="pointer-events-none absolute -left-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--green)]/[0.07] blur-3xl" />

      <div className="relative max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-[.55fr_.45fr] gap-10 items-end mb-14">
          <div className="min-w-0">
            <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
              &mdash; Estates Transformed
            </div>
            <h2 className="reveal font-display text-[32px] sm:text-[42px] md:text-[64px] leading-[1.02] text-[color:var(--parchment)]">
              Before, after,<br/>
              <span className="text-[color:var(--green)]">and everything between</span>.
            </h2>
          </div>
          <div className="reveal md:justify-self-end">
            <p className="text-[color:var(--parchment)]/70 leading-relaxed max-w-md">
              A look at recent estates before we arrived and after we finished &mdash; sorted, priced, staged, and ready. Every project reflects the same proven process, tailored to the family and property in front of us.
            </p>
          </div>
        </div>

        {/* Sculpture banner — every estate reshaped */}
        <figure className="reveal relative mb-14 md:mb-16 rounded-2xl overflow-hidden border border-white/10">
          <div className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9]">
            <img
              src="/estates-gears.webp?v=2"
              srcSet="/estates-gears-400w.webp 400w, /estates-gears-800w.webp 800w, /estates-gears.webp?v=2 2000w"
              sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px"
              alt="Ionic Estate Sales team members in branded shirts working through a home living room"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {/* Artifact Aperture treatment */}
            <div className="absolute inset-0 bg-[color:var(--obsidian)]/45" />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--obsidian)] via-[color:var(--obsidian)]/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--obsidian)]/70 via-transparent to-transparent" />

            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-10">
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div className="max-w-xl">
                  <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-2">
                    &mdash; Every estate, reshaped
                  </div>
                  <p className="font-display text-[15px] sm:text-[18px] md:text-[26px] leading-[1.2] text-[color:var(--parchment)]">
                    From cluttered chapter to <span className="text-[color:var(--green)]">curated composition</span> &mdash; that is the work.
                  </p>
                </div>
                <div className="hidden md:block font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/55">
                  Sorted · Priced · Staged · Sold
                </div>
              </div>
            </div>
          </div>
        </figure>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {estateTransformations.map((it, i) => (
            <BeforeAfterPair key={it.id} item={it} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EstatesTransformed;
